import { FormEvent, useEffect, useState } from "react";
import { ApiError } from "@/api/client";
import {
  backfillIntroMarkers,
  createLibrary,
  deleteLibrary,
  getAcquisitionOptions,
  getCapabilities,
  getConfig,
  getLibraries,
  testAcquisitionConnection,
  updateConfig,
  updateLibrary,
} from "@/api/endpoints";
import { useApi } from "@/hooks/useApi";
import type { AcquisitionRootFolder, Library, RequestResolution } from "@/types/api";

type AdminSettingsSection = "general" | "metadata" | "subtitles" | "acquisition" | "libraries";
type LibraryType = Library["attributes"]["library_type"];

const LIBRARY_TYPE_OPTIONS: { value: LibraryType; label: string }[] = [
  { value: "movies", label: "Movies" },
  { value: "tv", label: "Series" },
  { value: "music", label: "Music" },
  { value: "photos", label: "Photos" },
  { value: "audiobooks", label: "Audiobooks" },
  { value: "books", label: "Books" },
];

const RESOLUTION_OPTIONS: Array<{ value: RequestResolution; label: string }> = [
  { value: "sd", label: "SD" },
  { value: "720p", label: "720p" },
  { value: "1080p", label: "1080p" },
  { value: "4k", label: "4K" },
];

function parseOptionalInt(value: string): number {
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function AdminSettingsPage() {
  const caps = useApi(() => getCapabilities(), []);
  const config = useApi(() => getConfig(), []);
  const libraries = useApi(() => getLibraries(), []);

  const [section, setSection] = useState<AdminSettingsSection>("general");
  const [serverName, setServerName] = useState("KnotFin");
  const [defaultLanguage, setDefaultLanguage] = useState("en-US");
  const [tmdbApiKey, setTmdbApiKey] = useState("");
  const [rottenTomatoesKey, setRottenTomatoesKey] = useState("");
  const [externalDiscoveryUrl, setExternalDiscoveryUrl] = useState("");
  const [radarrEnabled, setRadarrEnabled] = useState(false);
  const [radarrUrl, setRadarrUrl] = useState("");
  const [radarrApiKey, setRadarrApiKey] = useState("");
  const [radarrRootFolderPath, setRadarrRootFolderPath] = useState("");
  const [radarrQualityProfileID, setRadarrQualityProfileID] = useState("");
  const [radarrDefaultResolution, setRadarrDefaultResolution] = useState<RequestResolution>("1080p");
  const [radarrMaxResolution, setRadarrMaxResolution] = useState<RequestResolution>("1080p");
  const [radarrMinimumAvailability, setRadarrMinimumAvailability] = useState("released");
  const [sonarrEnabled, setSonarrEnabled] = useState(false);
  const [sonarrUrl, setSonarrUrl] = useState("");
  const [sonarrApiKey, setSonarrApiKey] = useState("");
  const [sonarrRootFolderPath, setSonarrRootFolderPath] = useState("");
  const [sonarrQualityProfileID, setSonarrQualityProfileID] = useState("");
  const [sonarrDefaultResolution, setSonarrDefaultResolution] = useState<RequestResolution>("1080p");
  const [sonarrMaxResolution, setSonarrMaxResolution] = useState<RequestResolution>("1080p");
  const [sonarrLanguageProfileID, setSonarrLanguageProfileID] = useState("");
  const [opensubtitlesEnabled, setOpensubtitlesEnabled] = useState(false);
  const [opensubtitlesApiKey, setOpensubtitlesApiKey] = useState("");
  const [hasOpensubtitlesApiKey, setHasOpensubtitlesApiKey] = useState(false);
  const [opensubtitlesDefaultLanguage, setOpensubtitlesDefaultLanguage] = useState("en");
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingService, setTestingService] = useState<"radarr" | "sonarr" | null>(null);
  const [loadingOptions, setLoadingOptions] = useState<"radarr" | "sonarr" | null>(null);
  const [radarrRoots, setRadarrRoots] = useState<AcquisitionRootFolder[]>([]);
  const [sonarrRoots, setSonarrRoots] = useState<AcquisitionRootFolder[]>([]);
  const [configMessage, setConfigMessage] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [path, setPath] = useState("");
  const [libraryType, setLibraryType] = useState<LibraryType>("movies");
  const [savingLibrary, setSavingLibrary] = useState(false);
  const [editingID, setEditingID] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editPath, setEditPath] = useState("");
  const [editType, setEditType] = useState<LibraryType>("movies");
  const [updatingID, setUpdatingID] = useState<string | null>(null);
  const [removingID, setRemovingID] = useState<string | null>(null);
  const [introBackfilling, setIntroBackfilling] = useState<"missing" | "force" | null>(null);
  const [introMessage, setIntroMessage] = useState<string | null>(null);
  const [introError, setIntroError] = useState<string | null>(null);
  const [libraryMessage, setLibraryMessage] = useState<string | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);

  useEffect(() => {
    if (!config.data) return;
    const attrs = config.data.data.attributes;
    setServerName(attrs.server_name);
    setDefaultLanguage(attrs.default_language);
    setTmdbApiKey(attrs.tmdb_api_key ?? "");
    setRottenTomatoesKey("");
    setExternalDiscoveryUrl(attrs.external_discovery_url ?? "");
    setRadarrEnabled(Boolean(attrs.radarr_enabled));
    setRadarrUrl(attrs.radarr_url ?? "");
    setRadarrApiKey("");
    setRadarrRootFolderPath(attrs.radarr_root_folder_path ?? "");
    setRadarrQualityProfileID(String(attrs.radarr_quality_profile_id || ""));
    setRadarrDefaultResolution(attrs.radarr_default_resolution ?? "1080p");
    setRadarrMaxResolution(attrs.radarr_max_resolution ?? "1080p");
    setRadarrMinimumAvailability(attrs.radarr_minimum_availability ?? "released");
    setSonarrEnabled(Boolean(attrs.sonarr_enabled));
    setSonarrUrl(attrs.sonarr_url ?? "");
    setSonarrApiKey("");
    setSonarrRootFolderPath(attrs.sonarr_root_folder_path ?? "");
    setSonarrQualityProfileID(String(attrs.sonarr_quality_profile_id || ""));
    setSonarrDefaultResolution(attrs.sonarr_default_resolution ?? "1080p");
    setSonarrMaxResolution(attrs.sonarr_max_resolution ?? "1080p");
    setSonarrLanguageProfileID(String(attrs.sonarr_language_profile_id || ""));
    setOpensubtitlesEnabled(Boolean(attrs.opensubtitles_enabled));
    setOpensubtitlesApiKey("");
    setHasOpensubtitlesApiKey(Boolean(attrs.has_opensubtitles_api_key));
    setOpensubtitlesDefaultLanguage(attrs.opensubtitles_default_language ?? "en");
  }, [config.data]);

  async function handleConfigSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingConfig(true);
    setConfigMessage(null);
    setConfigError(null);
    try {
      const payload: Parameters<typeof updateConfig>[0] = {
        server_name: serverName.trim(),
        default_language: defaultLanguage,
        tmdb_api_key: tmdbApiKey.trim(),
        external_discovery_url: externalDiscoveryUrl.trim(),
        radarr_enabled: radarrEnabled,
        radarr_url: radarrUrl.trim(),
        radarr_api_key: radarrApiKey.trim(),
        radarr_root_folder_path: radarrRootFolderPath.trim(),
        radarr_quality_profile_id: parseOptionalInt(radarrQualityProfileID),
        radarr_default_resolution: radarrDefaultResolution,
        radarr_max_resolution: radarrMaxResolution,
        radarr_minimum_availability: radarrMinimumAvailability,
        sonarr_enabled: sonarrEnabled,
        sonarr_url: sonarrUrl.trim(),
        sonarr_api_key: sonarrApiKey.trim(),
        sonarr_root_folder_path: sonarrRootFolderPath.trim(),
        sonarr_quality_profile_id: parseOptionalInt(sonarrQualityProfileID),
        sonarr_default_resolution: sonarrDefaultResolution,
        sonarr_max_resolution: sonarrMaxResolution,
        sonarr_language_profile_id: parseOptionalInt(sonarrLanguageProfileID),
        opensubtitles_enabled: opensubtitlesEnabled,
        opensubtitles_default_language: opensubtitlesDefaultLanguage.trim(),
      };
      const trimmedOpenSubtitlesKey = opensubtitlesApiKey.trim();
      if (trimmedOpenSubtitlesKey !== "") {
        payload.opensubtitles_api_key = trimmedOpenSubtitlesKey;
      }
      const trimmedRottenTomatoesKey = rottenTomatoesKey.trim();
      if (trimmedRottenTomatoesKey !== "") {
        payload.rotten_tomatoes_key = trimmedRottenTomatoesKey;
      }
      const response = await updateConfig(payload);
      setServerName(response.data.attributes.server_name);
      setDefaultLanguage(response.data.attributes.default_language);
      setTmdbApiKey(response.data.attributes.tmdb_api_key ?? "");
      setRottenTomatoesKey("");
      setExternalDiscoveryUrl(response.data.attributes.external_discovery_url ?? "");
      setRadarrEnabled(Boolean(response.data.attributes.radarr_enabled));
      setRadarrUrl(response.data.attributes.radarr_url ?? "");
      setRadarrApiKey("");
      setRadarrRootFolderPath(response.data.attributes.radarr_root_folder_path ?? "");
      setRadarrQualityProfileID(String(response.data.attributes.radarr_quality_profile_id || ""));
      setRadarrDefaultResolution(response.data.attributes.radarr_default_resolution ?? "1080p");
      setRadarrMaxResolution(response.data.attributes.radarr_max_resolution ?? "1080p");
      setRadarrMinimumAvailability(response.data.attributes.radarr_minimum_availability ?? "released");
      setSonarrEnabled(Boolean(response.data.attributes.sonarr_enabled));
      setSonarrUrl(response.data.attributes.sonarr_url ?? "");
      setSonarrApiKey("");
      setSonarrRootFolderPath(response.data.attributes.sonarr_root_folder_path ?? "");
      setSonarrQualityProfileID(String(response.data.attributes.sonarr_quality_profile_id || ""));
      setSonarrDefaultResolution(response.data.attributes.sonarr_default_resolution ?? "1080p");
      setSonarrMaxResolution(response.data.attributes.sonarr_max_resolution ?? "1080p");
      setSonarrLanguageProfileID(String(response.data.attributes.sonarr_language_profile_id || ""));
      setOpensubtitlesEnabled(Boolean(response.data.attributes.opensubtitles_enabled));
      setOpensubtitlesApiKey("");
      setHasOpensubtitlesApiKey(Boolean(response.data.attributes.has_opensubtitles_api_key));
      setOpensubtitlesDefaultLanguage(response.data.attributes.opensubtitles_default_language ?? "en");
      setConfigMessage("Server settings saved");
      config.refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        setConfigError(err.problem.detail);
      } else {
        setConfigError("Failed to save server settings");
      }
    } finally {
      setSavingConfig(false);
    }
  }

  async function handleTestAcquisition(service: "radarr" | "sonarr") {
    setTestingService(service);
    setConfigMessage(null);
    setConfigError(null);
    try {
      await testAcquisitionConnection({ service });
      setConfigMessage(`${service === "radarr" ? "Radarr" : "Sonarr"} connection works`);
    } catch (err) {
      if (err instanceof ApiError) {
        setConfigError(err.problem.detail);
      } else {
        setConfigError(`Failed to test ${service === "radarr" ? "Radarr" : "Sonarr"}`);
      }
    } finally {
      setTestingService(null);
    }
  }

  async function handleLoadAcquisitionOptions(service: "radarr" | "sonarr") {
    setLoadingOptions(service);
    setConfigMessage(null);
    setConfigError(null);
    try {
      const response = await getAcquisitionOptions(service);
      if (service === "radarr") {
        setRadarrRoots(response.meta.root_folders);
      } else {
        setSonarrRoots(response.meta.root_folders);
      }
      setConfigMessage(`${service === "radarr" ? "Radarr" : "Sonarr"} profiles loaded`);
    } catch (err) {
      if (err instanceof ApiError) {
        setConfigError(err.problem.detail);
      } else {
        setConfigError(`Failed to load ${service === "radarr" ? "Radarr" : "Sonarr"} profiles`);
      }
    } finally {
      setLoadingOptions(null);
    }
  }

  async function handleCreateLibrary(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingLibrary(true);
    setLibraryMessage(null);
    setLibraryError(null);
    try {
      await createLibrary({
        name: name.trim(),
        library_type: libraryType,
        source: {
          kind: "filesystem",
          path: path.trim(),
        },
      });
      setName("");
      setPath("");
      setLibraryType("movies");
      setLibraryMessage("Library added");
      libraries.refetch();
      window.dispatchEvent(new Event("knotfin:libraries-changed"));
    } catch (err) {
      if (err instanceof ApiError) {
        setLibraryError(err.problem.detail);
      } else {
        setLibraryError("Failed to add library");
      }
    } finally {
      setSavingLibrary(false);
    }
  }

  async function handleRemoveLibrary(libraryID: string, libraryName: string) {
    if (!window.confirm(`Remove library "${libraryName}"?`)) {
      return;
    }
    setRemovingID(libraryID);
    setLibraryMessage(null);
    setLibraryError(null);
    try {
      await deleteLibrary(libraryID);
      setLibraryMessage("Library removed");
      libraries.refetch();
      window.dispatchEvent(new Event("knotfin:libraries-changed"));
    } catch (err) {
      if (err instanceof ApiError) {
        setLibraryError(err.problem.detail);
      } else {
        setLibraryError("Failed to remove library");
      }
    } finally {
      setRemovingID(null);
    }
  }

  async function handleBackfillIntros(mode: "missing" | "force") {
    if (mode === "force" && !window.confirm("Re-analyze skip markers for every season? This can take a while.")) {
      return;
    }
    setIntroBackfilling(mode);
    setIntroMessage(null);
    setIntroError(null);
    try {
      await backfillIntroMarkers(mode);
      setIntroMessage(
        mode === "force"
          ? "Started a full skip-marker re-scan of all seasons. Watch progress on the Jobs page."
          : "Started skip-marker detection for seasons with missing intro markers. Watch progress on the Jobs page.",
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setIntroError(err.problem.detail);
      } else {
        setIntroError("Failed to start skip-marker backfill");
      }
    } finally {
      setIntroBackfilling(null);
    }
  }

  function beginEdit(library: Library) {
    setEditingID(library.id);
    setEditName(library.attributes.name);
    setEditPath(library.attributes.source_path ?? "");
    setEditType(library.attributes.library_type);
    setLibraryMessage(null);
    setLibraryError(null);
  }

  function cancelEdit() {
    setEditingID(null);
    setEditName("");
    setEditPath("");
    setEditType("movies");
  }

  async function handleUpdateLibrary(libraryID: string) {
    setUpdatingID(libraryID);
    setLibraryMessage(null);
    setLibraryError(null);
    try {
      await updateLibrary(libraryID, {
        name: editName.trim(),
        library_type: editType,
        source: {
          path: editPath.trim(),
        },
      });
      setLibraryMessage("Library updated");
      cancelEdit();
      libraries.refetch();
      window.dispatchEvent(new Event("knotfin:libraries-changed"));
    } catch (err) {
      if (err instanceof ApiError) {
        setLibraryError(err.problem.detail);
      } else {
        setLibraryError("Failed to update library");
      }
    } finally {
      setUpdatingID(null);
    }
  }

  return (
    <div className="admin-page settings-page">
      <div className="settings-header">
        <h1>Server Settings</h1>
        <p className="settings-copy admin-page-copy">
          Plex-style organization: server behavior on the left, one focused category at a time on the right.
        </p>
      </div>

      <div className="settings-shell">
        <aside className="settings-nav">
          <div className="settings-nav-group">
            <div className="settings-nav-label">Server</div>
            <button
              type="button"
              className={`settings-nav-item${section === "general" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("general")}
            >
              General
            </button>
            <button
              type="button"
              className={`settings-nav-item${section === "metadata" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("metadata")}
            >
              Metadata
            </button>
            <button
              type="button"
              className={`settings-nav-item${section === "subtitles" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("subtitles")}
            >
              Subtitles
            </button>
            <button
              type="button"
              className={`settings-nav-item${section === "acquisition" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("acquisition")}
            >
              Radarr/Sonarr
            </button>
          </div>

          <div className="settings-nav-group">
            <div className="settings-nav-label">Manage</div>
            <button
              type="button"
              className={`settings-nav-item${section === "libraries" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("libraries")}
            >
              Libraries
            </button>
          </div>
        </aside>

        <div className="settings-panel">
          {(section === "general" || section === "metadata" || section === "subtitles" || section === "acquisition") && (
            <form className="settings-form settings-form--stacked" onSubmit={handleConfigSubmit}>
              {section === "general" && (
                <section className="settings-section">
                  <div className="settings-section-header">
                    <div>
                      <h2>General</h2>
                      <p className="settings-note">Server identity and defaults used by every client and scan job.</p>
                    </div>
                  </div>

                  <div className="settings-row">
                    <span className="settings-label">Server Name</span>
                    <input
                      className="settings-input"
                      type="text"
                      value={serverName}
                      onChange={(event) => setServerName(event.target.value)}
                    />
                  </div>

                  <div className="settings-row">
                    <span className="settings-label">Default Language</span>
                    <select
                      className="settings-select"
                      value={defaultLanguage}
                      onChange={(event) => setDefaultLanguage(event.target.value)}
                    >
                      <option value="en-US">English (US)</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>

                  {caps.data && (
                    <div className="settings-info-grid">
                      <div><strong>Server Version:</strong> {caps.data.data.attributes.server_version}</div>
                      <div><strong>API Version:</strong> {caps.data.data.attributes.api_version}</div>
                      <div><strong>Auth Methods:</strong> {caps.data.data.attributes.auth_methods.join(", ")}</div>
                      <div><strong>Playback Protocols:</strong> {caps.data.data.attributes.playback_protocols.join(", ")}</div>
                    </div>
                  )}

                  <div className="settings-actions">
                    <button className="btn btn--primary" type="submit" disabled={savingConfig}>
                      {savingConfig ? "Saving..." : "Save Changes"}
                    </button>
                  </div>

                  {configMessage && <p className="settings-status settings-status--success">{configMessage}</p>}
                  {configError && <p className="settings-status settings-status--error">{configError}</p>}
                </section>
              )}

              {section === "metadata" && (
                <section className="settings-section">
                  <div className="settings-section-header">
                    <div>
                      <h2>Metadata</h2>
                      <p className="settings-note">Provider credentials for artwork, matching, and background metadata refreshes.</p>
                    </div>
                    <div className="settings-badge-row">
                      {config.data?.data.attributes.has_tmdb_api_key && <span className="badge">TMDb Key Saved</span>}
                      {config.data?.data.attributes.has_rotten_tomatoes_key && <span className="badge">RT Key Saved</span>}
                    </div>
                  </div>

                  <label className="settings-field">
                    <span>TMDb API Key</span>
                    <input
                      className="settings-input"
                      type="password"
                      autoComplete="off"
                      spellCheck={false}
                      value={tmdbApiKey}
                      onChange={(event) => setTmdbApiKey(event.target.value)}
                      placeholder="Enter the server TMDb API key"
                    />
                  </label>

                  <p className="settings-help">
                    Stored once at the server level so imports, rescans, and repair tasks work even when no user is actively logged in.
                  </p>

                  <label className="settings-field">
                    <span>Rotten Tomatoes Integration Key</span>
                    <input
                      className="settings-input"
                      type="password"
                      autoComplete="off"
                      spellCheck={false}
                      value={rottenTomatoesKey}
                      onChange={(event) => setRottenTomatoesKey(event.target.value)}
                      placeholder="Enter an upstream discovery key"
                    />
                  </label>

                  <label className="settings-field">
                    <span>External Discovery URL</span>
                    <input
                      className="settings-input"
                      type="url"
                      value={externalDiscoveryUrl}
                      onChange={(event) => setExternalDiscoveryUrl(event.target.value)}
                      placeholder="https://discovery.example.test"
                    />
                  </label>

                  <div className="settings-actions">
                    <button className="btn btn--primary" type="submit" disabled={savingConfig}>
                      {savingConfig ? "Saving..." : "Save Changes"}
                    </button>
                  </div>

                  {configMessage && <p className="settings-status settings-status--success">{configMessage}</p>}
                  {configError && <p className="settings-status settings-status--error">{configError}</p>}
                </section>
              )}

              {section === "subtitles" && (
                <section className="settings-section">
                  <div className="settings-section-header">
                    <div>
                      <h2>Subtitles</h2>
                      <p className="settings-note">
                        Find and download subtitles from OpenSubtitles when local sidecar files or embedded tracks aren&apos;t available.
                      </p>
                    </div>
                    {hasOpensubtitlesApiKey && (
                      <div className="settings-badge-row">
                        <span className="badge">OpenSubtitles Key Saved</span>
                      </div>
                    )}
                  </div>

                  <label className="settings-checkbox">
                    <input
                      type="checkbox"
                      checked={opensubtitlesEnabled}
                      onChange={(event) => setOpensubtitlesEnabled(event.target.checked)}
                    />
                    <span>Enable OpenSubtitles provider</span>
                  </label>

                  <label className="settings-field">
                    <span>OpenSubtitles API Key</span>
                    <input
                      className="settings-input"
                      type="password"
                      autoComplete="off"
                      spellCheck={false}
                      value={opensubtitlesApiKey}
                      onChange={(event) => setOpensubtitlesApiKey(event.target.value)}
                      placeholder={hasOpensubtitlesApiKey ? "Saved — leave blank to keep" : "Enter your OpenSubtitles API key"}
                    />
                  </label>
                  <p className="settings-help">
                    Get a key at <a href="https://www.opensubtitles.com/en/consumers" target="_blank" rel="noreferrer">opensubtitles.com</a>. The key is stored at the server level and never sent back to the browser.
                  </p>

                  <label className="settings-field">
                    <span>Default Language</span>
                    <input
                      className="settings-input"
                      type="text"
                      value={opensubtitlesDefaultLanguage}
                      onChange={(event) => setOpensubtitlesDefaultLanguage(event.target.value)}
                      placeholder="en"
                    />
                  </label>
                  <p className="settings-help">ISO 639-1 code (e.g. <code>en</code>, <code>es</code>, <code>fr</code>).</p>

                  <div className="settings-actions">
                    <button className="btn btn--primary" type="submit" disabled={savingConfig}>
                      {savingConfig ? "Saving..." : "Save Changes"}
                    </button>
                  </div>

                  {configMessage && <p className="settings-status settings-status--success">{configMessage}</p>}
                  {configError && <p className="settings-status settings-status--error">{configError}</p>}
                </section>
              )}

              {section === "acquisition" && (
                <section className="settings-section">
                  <div className="settings-section-header">
                    <div>
                      <h2>Radarr/Sonarr</h2>
                      <p className="settings-note">
                        Movie requests can be sent to Radarr and series requests can be sent to Sonarr.
                      </p>
                    </div>
                    <div className="settings-badge-row">
                      {config.data?.data.attributes.has_radarr_api_key && <span className="badge">Radarr Key Saved</span>}
                      {config.data?.data.attributes.has_sonarr_api_key && <span className="badge">Sonarr Key Saved</span>}
                    </div>
                  </div>

                  <h3>Radarr</h3>
                  <label className="settings-checkbox">
                    <input
                      type="checkbox"
                      checked={radarrEnabled}
                      onChange={(event) => setRadarrEnabled(event.target.checked)}
                    />
                    <span>Send movie requests to Radarr</span>
                  </label>

                  <div className="settings-grid">
                    <label className="settings-field">
                      <span>Radarr URL</span>
                      <input
                        className="settings-input"
                        type="url"
                        value={radarrUrl}
                        onChange={(event) => setRadarrUrl(event.target.value)}
                        placeholder="http://radarr:7878"
                      />
                    </label>
                    <label className="settings-field">
                      <span>Radarr API Key</span>
                      <input
                        className="settings-input"
                        type="password"
                        aria-label="Radarr API Key"
                        autoComplete="off"
                        spellCheck={false}
                        value={radarrApiKey}
                        onChange={(event) => setRadarrApiKey(event.target.value)}
                        placeholder="Leave blank to keep saved key"
                      />
                      <small className="settings-help">
                        {config.data?.data.attributes.has_radarr_api_key ? "API key saved. Leave blank to keep it." : "No API key saved."}
                      </small>
                    </label>
                    <label className="settings-field">
                      <span>Movie Root Folder</span>
                      {radarrRoots.length > 0 ? (
                        <select
                          className="settings-select"
                          value={radarrRootFolderPath}
                          onChange={(event) => setRadarrRootFolderPath(event.target.value)}
                        >
                          <option value="">Select a movie root</option>
                          {radarrRoots.map((root) => (
                            <option key={root.id} value={root.attributes.path}>{root.attributes.path}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="settings-input"
                          type="text"
                          value={radarrRootFolderPath}
                          onChange={(event) => setRadarrRootFolderPath(event.target.value)}
                          placeholder="/movies"
                        />
                      )}
                    </label>
                    <label className="settings-field">
                      <span>Default Movie Resolution</span>
                      <select
                        className="settings-select"
                        value={radarrDefaultResolution}
                        onChange={(event) => setRadarrDefaultResolution(event.target.value as RequestResolution)}
                      >
                        {RESOLUTION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="settings-field">
                      <span>Max Movie Resolution</span>
                      <select
                        className="settings-select"
                        value={radarrMaxResolution}
                        onChange={(event) => setRadarrMaxResolution(event.target.value as RequestResolution)}
                      >
                        {RESOLUTION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="settings-field">
                      <span>Minimum Availability</span>
                      <select
                        className="settings-select"
                        value={radarrMinimumAvailability}
                        onChange={(event) => setRadarrMinimumAvailability(event.target.value)}
                      >
                        <option value="announced">Announced</option>
                        <option value="inCinemas">In Cinemas</option>
                        <option value="released">Released</option>
                      </select>
                    </label>
                  </div>

                  <h3>Sonarr</h3>
                  <label className="settings-checkbox">
                    <input
                      type="checkbox"
                      checked={sonarrEnabled}
                      onChange={(event) => setSonarrEnabled(event.target.checked)}
                    />
                    <span>Send series requests to Sonarr</span>
                  </label>

                  <div className="settings-grid">
                    <label className="settings-field">
                      <span>Sonarr URL</span>
                      <input
                        className="settings-input"
                        type="url"
                        value={sonarrUrl}
                        onChange={(event) => setSonarrUrl(event.target.value)}
                        placeholder="http://sonarr:8989"
                      />
                    </label>
                    <label className="settings-field">
                      <span>Sonarr API Key</span>
                      <input
                        className="settings-input"
                        type="password"
                        aria-label="Sonarr API Key"
                        autoComplete="off"
                        spellCheck={false}
                        value={sonarrApiKey}
                        onChange={(event) => setSonarrApiKey(event.target.value)}
                        placeholder="Leave blank to keep saved key"
                      />
                      <small className="settings-help">
                        {config.data?.data.attributes.has_sonarr_api_key ? "API key saved. Leave blank to keep it." : "No API key saved."}
                      </small>
                    </label>
                    <label className="settings-field">
                      <span>Series Root Folder</span>
                      {sonarrRoots.length > 0 ? (
                        <select
                          className="settings-select"
                          value={sonarrRootFolderPath}
                          onChange={(event) => setSonarrRootFolderPath(event.target.value)}
                        >
                          <option value="">Select a series root</option>
                          {sonarrRoots.map((root) => (
                            <option key={root.id} value={root.attributes.path}>{root.attributes.path}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="settings-input"
                          type="text"
                          value={sonarrRootFolderPath}
                          onChange={(event) => setSonarrRootFolderPath(event.target.value)}
                          placeholder="/series"
                        />
                      )}
                    </label>
                    <label className="settings-field">
                      <span>Default Series Resolution</span>
                      <select
                        className="settings-select"
                        value={sonarrDefaultResolution}
                        onChange={(event) => setSonarrDefaultResolution(event.target.value as RequestResolution)}
                      >
                        {RESOLUTION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="settings-field">
                      <span>Max Series Resolution</span>
                      <select
                        className="settings-select"
                        value={sonarrMaxResolution}
                        onChange={(event) => setSonarrMaxResolution(event.target.value as RequestResolution)}
                      >
                        {RESOLUTION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="settings-field">
                      <span>Language Profile ID</span>
                      <input
                        className="settings-input"
                        type="number"
                        min="0"
                        value={sonarrLanguageProfileID}
                        onChange={(event) => setSonarrLanguageProfileID(event.target.value)}
                        placeholder="Required by Sonarr v3"
                      />
                    </label>
                  </div>

                  <div className="settings-actions">
                    <button className="btn btn--primary" type="submit" disabled={savingConfig}>
                      {savingConfig ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      className="btn btn--secondary"
                      type="button"
                      disabled={testingService !== null}
                      onClick={() => void handleTestAcquisition("radarr")}
                    >
                      {testingService === "radarr" ? "Testing..." : "Test Radarr"}
                    </button>
                    <button
                      className="btn btn--secondary"
                      type="button"
                      disabled={loadingOptions !== null}
                      onClick={() => void handleLoadAcquisitionOptions("radarr")}
                    >
                      {loadingOptions === "radarr" ? "Loading..." : "Load Radarr Profiles"}
                    </button>
                    <button
                      className="btn btn--secondary"
                      type="button"
                      disabled={testingService !== null}
                      onClick={() => void handleTestAcquisition("sonarr")}
                    >
                      {testingService === "sonarr" ? "Testing..." : "Test Sonarr"}
                    </button>
                    <button
                      className="btn btn--secondary"
                      type="button"
                      disabled={loadingOptions !== null}
                      onClick={() => void handleLoadAcquisitionOptions("sonarr")}
                    >
                      {loadingOptions === "sonarr" ? "Loading..." : "Load Sonarr Profiles"}
                    </button>
                  </div>

                  {configMessage && <p className="settings-status settings-status--success">{configMessage}</p>}
                  {configError && <p className="settings-status settings-status--error">{configError}</p>}
                </section>
              )}
            </form>
          )}

          {section === "libraries" && (
            <section className="settings-section">
              <div className="settings-section-header">
                <div>
                  <h2>Libraries</h2>
                  <p className="settings-note">
                    Add, edit, and remove media roots. The path must be absolute and visible inside the KnotFin container.
                  </p>
                </div>
              </div>

              <form className="settings-form settings-form--stacked" onSubmit={handleCreateLibrary}>
                <div className="settings-grid">
                  <label className="settings-field">
                    <span>Name</span>
                    <input
                      className="settings-input"
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="Movies"
                      required
                    />
                  </label>
                  <label className="settings-field">
                    <span>Type</span>
                    <select
                      className="settings-select"
                      value={libraryType}
                      onChange={(event) => setLibraryType(event.target.value as LibraryType)}
                    >
                      {LIBRARY_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="settings-field">
                  <span>Path</span>
                  <input
                    className="settings-input"
                    type="text"
                    value={path}
                    onChange={(event) => setPath(event.target.value)}
                    placeholder="/media/movies"
                    required
                  />
                </label>

                <div className="settings-actions">
                  <button className="btn btn--primary" type="submit" disabled={savingLibrary}>
                    {savingLibrary ? "Adding..." : "Add Library"}
                  </button>
                </div>
              </form>

              {libraryMessage && <p className="settings-status settings-status--success">{libraryMessage}</p>}
              {libraryError && <p className="settings-status settings-status--error">{libraryError}</p>}

              <div className="settings-library-list">
                {libraries.loading && <p>Loading libraries...</p>}
                {libraries.data?.data.map((library) => (
                  <div key={library.id} className="settings-library-item">
                    {editingID === library.id ? (
                      <div className="settings-library-edit">
                        <div className="settings-grid">
                          <label className="settings-field">
                            <span>Name</span>
                            <input
                              className="settings-input"
                              type="text"
                              value={editName}
                              onChange={(event) => setEditName(event.target.value)}
                            />
                          </label>
                          <label className="settings-field">
                            <span>Type</span>
                            <select
                              className="settings-select"
                              value={editType}
                              onChange={(event) => setEditType(event.target.value as LibraryType)}
                            >
                              {LIBRARY_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>

                        <label className="settings-field">
                          <span>Path</span>
                          <input
                            className="settings-input"
                            type="text"
                            value={editPath}
                            onChange={(event) => setEditPath(event.target.value)}
                          />
                        </label>

                        <div className="settings-actions">
                          <button
                            className="btn btn--primary"
                            type="button"
                            disabled={updatingID === library.id}
                            onClick={() => void handleUpdateLibrary(library.id)}
                          >
                            {updatingID === library.id ? "Saving..." : "Save"}
                          </button>
                          <button className="btn btn--secondary" type="button" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="settings-library-meta">
                          <div className="settings-library-title-row">
                            <strong>{library.attributes.name}</strong>
                            <span className="badge">{libraryTypeLabel(library.attributes.library_type)}</span>
                          </div>
                          <div className="settings-library-path">{library.attributes.source_path || "No path configured"}</div>
                        </div>

                        <div className="settings-library-actions">
                          <button
                            className="btn btn--secondary"
                            type="button"
                            onClick={() => beginEdit(library)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn--secondary"
                            type="button"
                            disabled={removingID === library.id}
                            onClick={() => void handleRemoveLibrary(library.id, library.attributes.name)}
                          >
                            {removingID === library.id ? "Removing..." : "Remove"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className="settings-section-header" style={{ marginTop: "1.5rem" }}>
                <div>
                  <h2>Skip markers</h2>
                  <p className="settings-note">
                    New episodes are analyzed for intros and chapter-title credits automatically against their season.
                    Use these to backfill existing shows. Detection runs in the background — watch progress on the Jobs page.
                  </p>
                </div>
              </div>
              {introMessage && <p className="settings-status settings-status--success">{introMessage}</p>}
              {introError && <p className="settings-status settings-status--error">{introError}</p>}
              <div className="settings-actions">
                <button
                  className="btn btn--primary"
                  type="button"
                  disabled={introBackfilling !== null}
                  onClick={() => void handleBackfillIntros("missing")}
                >
                  {introBackfilling === "missing" ? "Starting..." : "Backfill missing"}
                </button>
                <button
                  className="btn btn--secondary"
                  type="button"
                  disabled={introBackfilling !== null}
                  onClick={() => void handleBackfillIntros("force")}
                >
                  {introBackfilling === "force" ? "Starting..." : "Force re-scan all"}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function libraryTypeLabel(value: LibraryType): string {
  return LIBRARY_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}
